import React, { useState, useEffect } from 'react';
import { Trophy, TrendingUp, Award, Target, Zap, Star, Crown, Lock } from 'lucide-react';

const GamificationDashboard = () => {
  const [xpData, setXpData] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchGamificationData();
  }, []);

  const fetchGamificationData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch XP data
      const xpResponse = await fetch(`${BACKEND_URL}/api/member/gamification/xp`, { headers });
      const xpResult = await xpResponse.json();
      if (xpResult.ok) {
        setXpData(xpResult.xp);
      }

      // Fetch achievements
      const achievementsResponse = await fetch(`${BACKEND_URL}/api/member/gamification/achievements`, { headers });
      const achievementsResult = await achievementsResponse.json();
      if (achievementsResult.ok) {
        setAchievements(achievementsResult.achievements);
      }

      // Fetch leaderboard
      const leaderboardResponse = await fetch(`${BACKEND_URL}/api/member/gamification/leaderboard?limit=10`, { headers });
      const leaderboardResult = await leaderboardResponse.json();
      if (leaderboardResult.ok) {
        setLeaderboard(leaderboardResult.leaderboard);
      }
    } catch (error) {
      console.error('Error fetching gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelIcon = (level) => {
    const icons = {
      'rookie': '🌱',
      'builder': '🔨',
      'pro': '⚡',
      'expert': '🎯',
      'master': '👑',
      'legend': '🏆'
    };
    return icons[level] || '🌱';
  };

  const getLevelColor = (level) => {
    const colors = {
      'rookie': 'from-green-400 to-green-600',
      'builder': 'from-blue-400 to-blue-600',
      'pro': 'from-purple-400 to-purple-600',
      'expert': 'from-orange-400 to-orange-600',
      'master': 'from-yellow-400 to-yellow-600',
      'legend': 'from-red-400 to-red-600'
    };
    return colors[level] || 'from-gray-400 to-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  const completedAchievements = achievements.filter(a => a.is_completed);
  const inProgressAchievements = achievements.filter(a => !a.is_completed);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🎮 Your Progress</h1>
          <p className="text-gray-600">Track your journey, earn achievements, and level up!</p>
        </div>

        {/* XP & Level Card */}
        {xpData && (
          <div className={`bg-gradient-to-r ${getLevelColor(xpData.current_level)} rounded-2xl shadow-2xl p-8 text-white mb-8`}>
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-6 mb-6 md:mb-0">
                <div className="text-6xl">{getLevelIcon(xpData.current_level)}</div>
                <div>
                  <div className="text-sm opacity-90 uppercase tracking-wide">Your Level</div>
                  <h2 className="text-4xl font-bold capitalize">{xpData.current_level}</h2>
                  <p className="text-lg opacity-90">Level {xpData.level_number}</p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <div className="text-5xl font-bold mb-2">{xpData.total_xp.toLocaleString()}</div>
                <div className="text-sm opacity-90">Total XP</div>
                {xpData.level_number < 6 && (
                  <p className="text-sm mt-2 opacity-80">
                    {xpData.xp_to_next_level} XP to next level
                  </p>
                )}
              </div>
            </div>
            
            {/* Progress Bar */}
            {xpData.level_number < 6 && (
              <div className="mt-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span>Level Progress</span>
                  <span>{xpData.xp_progress_percent}%</span>
                </div>
                <div className="bg-white bg-opacity-20 rounded-full h-4">
                  <div
                    className="bg-white rounded-full h-4 transition-all duration-500"
                    style={{ width: `${xpData.xp_progress_percent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Achievements Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Trophy className="h-6 w-6 mr-2 text-yellow-600" />
                  Achievements
                </h2>
                <div className="text-sm text-gray-600">
                  {completedAchievements.length} / {achievements.length} Unlocked
                </div>
              </div>

              {/* Completed Achievements */}
              {completedAchievements.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">🎉 Unlocked</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {completedAchievements.map((item, idx) => (
                      <div
                        key={idx}
                        className="border-2 border-green-200 bg-green-50 rounded-lg p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="text-3xl">{item.achievement.icon}</div>
                          <Award className="h-5 w-5 text-green-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">{item.achievement.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{item.achievement.description}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{item.achievement.category}</span>
                          {item.achievement.xp_reward > 0 && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                              +{item.achievement.xp_reward} XP
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* In Progress Achievements */}
              {inProgressAchievements.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 In Progress</h3>
                  <div className="space-y-4">
                    {inProgressAchievements.map((item, idx) => (
                      <div
                        key={idx}
                        className="border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start space-x-3">
                            <div className="text-2xl opacity-50">{item.achievement.icon}</div>
                            <div>
                              <h4 className="font-semibold text-gray-900">{item.achievement.name}</h4>
                              <p className="text-sm text-gray-600">{item.achievement.description}</p>
                            </div>
                          </div>
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Progress</span>
                            <span className="font-medium">{item.progress} / {item.required}</span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-indigo-600 rounded-full h-2 transition-all duration-500"
                              style={{ width: `${(item.progress / item.required) * 100}%` }}
                            />
                          </div>
                          {item.achievement.xp_reward > 0 && (
                            <div className="text-xs text-gray-500">
                              Reward: +{item.achievement.xp_reward} XP
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-6 text-white">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Zap className="h-5 w-5 mr-2" />
                Earn More XP
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                <a
                  href="/member/lead-capture/marketplace"
                  className="bg-white bg-opacity-20 rounded-lg p-3 hover:bg-opacity-30 transition-all"
                >
                  <div className="font-semibold mb-1">📄 Publish a Page</div>
                  <div className="text-sm opacity-90">+100 XP</div>
                </a>
                <a
                  href="/qr-generator"
                  className="bg-white bg-opacity-20 rounded-lg p-3 hover:bg-opacity-30 transition-all"
                >
                  <div className="font-semibold mb-1">📱 Create QR Code</div>
                  <div className="text-sm opacity-90">+25 XP</div>
                </a>
                <a
                  href="/member/boot-camp-vault"
                  className="bg-white bg-opacity-20 rounded-lg p-3 hover:bg-opacity-30 transition-all"
                >
                  <div className="font-semibold mb-1">📚 Complete Training</div>
                  <div className="text-sm opacity-90">+75 XP</div>
                </a>
                <a
                  href="/member/founders"
                  className="bg-white bg-opacity-20 rounded-lg p-3 hover:bg-opacity-30 transition-all"
                >
                  <div className="font-semibold mb-1">👥 Refer a Friend</div>
                  <div className="text-sm opacity-90">+150 XP</div>
                </a>
              </div>
            </div>
          </div>

          {/* Leaderboard Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Crown className="h-6 w-6 mr-2 text-yellow-600" />
                Top Members
              </h2>
              
              {leaderboard.length > 0 ? (
                <div className="space-y-3">
                  {leaderboard.map((entry, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                        idx < 3
                          ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className={`text-2xl font-bold ${
                        idx === 0 ? 'text-yellow-600' :
                        idx === 1 ? 'text-gray-400' :
                        idx === 2 ? 'text-orange-600' :
                        'text-gray-400'
                      }`}>
                        #{entry.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">
                          {entry.full_name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {entry.value.toLocaleString()} XP
                        </div>
                      </div>
                      {idx < 3 && (
                        <div className="text-2xl">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Leaderboard loading...</p>
                </div>
              )}

              <a
                href="/member/leaderboard"
                className="mt-6 block text-center text-indigo-600 hover:text-indigo-800 font-medium text-sm"
              >
                View Full Leaderboard →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamificationDashboard;
